import React, { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Box, Button, Modal } from '@mui/material'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import MessageCard from '~/src/components/molecules/Message/MessageCard'
import { orderRepository } from '~/src/repositories'
import { MessageForm } from '~/src/components/organisms/ContainerOrder/Message/MessageForm'
import { useMessage } from '~/src/hooks'
import { IPostRequestOrderMessageRequestBody } from '~/src/repositories/Order/models/Order'
import { Close } from '~/src/components/atoms/SvgComponent/Close'
import { useRequestOrderMessages } from '~/src/hooks/useRequestOrderMessages'
import { RequestOrderMessage } from '~/src/repositories/Order/models/Order'
import { UnreadLine } from '~/src/components/atoms/UnreadLine'

interface MessageModalProps {
  isOpen: boolean
  handleClose: () => void
  id: string
}

export const MessageModal: React.FC<MessageModalProps> = ({ isOpen, handleClose, id }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { handleOpenWithErrorMessage, handleOpenWithSuccessMessage } = useMessage()
  const { data, isLoading, isError, mutate } = useRequestOrderMessages(id)
  const [isPosting, setIsPosting] = useState(false)
  const onSubmitMessage = async (values: IPostRequestOrderMessageRequestBody) => {
    try {
      setIsPosting(true)
      await orderRepository.postRequestOrderMessage(Number(id), values)
      await mutate()
      handleOpenWithSuccessMessage('送信しました')
    } catch (e: any) {
      handleOpenWithErrorMessage(e.message)
    } finally {
      setIsPosting(false)
    }
  }

  const handleShowAll = () => {
    navigate(`/admin/order/contact-order/detail/${id}`, {
      state: {
        prevLocationState: { ...location },
      },
    })
  }

  if (isError) {
    handleOpenWithErrorMessage('メッセージの取得に失敗しました')
  }

  const [showDialog, setShowDialog] = useState(false)
  const [commentId, setCommentId] = useState('')

  const deleteComment = async (commentId: string) => {
    setCommentId(commentId)
    setShowDialog(true)
  }

  let messages: RequestOrderMessage[] = [];
  if (data?.messages) {
    messages = data?.messages.slice(0,4);
  }

  // メッセージをidの昇順(古い順)にし、一番最初の未読メッセージのindexを取得する
  messages.sort((current, next) => current.id - next.id)
  const index = messages.findIndex((message) => message.isUnread === true);

  // column-reverseで表示するため、並び順を元に戻す
  messages.sort((current, next) => next.id - current.id)

  // 未読メッセージと既読メッセージを分ける
  // index が -1 の場合は未読メッセージが存在しない = メッセージが0件 or 既読メッセージしかない
  const messagesLength = messages.length;
  let unreadMessages: RequestOrderMessage[] = [];
  let readMessages: RequestOrderMessage[] = [];
  if (messagesLength !== 0) {
    if (index === -1) {
      readMessages = messages;
    } else {
      unreadMessages = messages.slice(0, messagesLength - index);
      readMessages = messages.slice(messagesLength - index);
    }
  }

  return (
    <Modal open={isOpen} onClose={handleClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 630,
          maxHeight: 920,
          height: 'auto',
          bgcolor: 'white',
        }}
      >
        <Box
          sx={{
            width: '100%',
            height: 56,
            p: 3,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Box
            onClick={handleClose}
            sx={{
              display: 'flex',
              height: '14px',
              cursor: 'pointer',
            }}
          >
            <Close />
          </Box>
        </Box>
        <Button
          sx={{
            width: '100%',
            height: 48,
            bgcolor: '#F7F7F7',
            fontFamily: 'Poppins',
            fontSize: 'sm',
            fontStyle: 'normal',
            fontWeight: 600,
            lineHeight: 18,
          }}
          endIcon={<ArrowForwardIosIcon />}
          onClick={handleShowAll}
        >
          すべての履歴を見る
        </Button>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column-reverse', //スクロールの初期位置を最下部にするため
            maxHeight: 250,
            overflowY: 'auto',
          }}
        >
          { !!unreadMessages.length && (
            <>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column-reverse', //スクロールの初期位置を最下部にするため
              }}>
                <MessageCard
                  id={id}
                  messages={unreadMessages}
                  isLoading={isLoading}
                  isShowAll={false}
                  isReversed={true}
                  onDelete={deleteComment}
                  isModal={isOpen}
                />
              </Box>
              <UnreadLine />
            </>
          )}
          { !!readMessages.length && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column-reverse', //スクロールの初期位置を最下部にするため
                '& .MuiBox-root:first-of-type': {
                  borderBottom: 'none',
                }
            }}>
              <MessageCard
                id={id}
                messages={readMessages}
                isLoading={isLoading}
                isShowAll={false}
                isReversed={true}
                onDelete={deleteComment}
                isModal={isOpen}
              />
            </Box>
          )}
        </Box>
        <Box m={3}>
          <MessageForm onSubmit={onSubmitMessage} isPosting={isPosting} />
        </Box>
      </Box>
    </Modal>
  )
}
